import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { fetchNotificationGroups } from '@rpforms/shared/build/actions/notification_groups';
import { Loader } from '@rpforms/shared/build/components/universal/Loader';

const GroupLinksContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StyledNavLink = styled(NavLink)`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background-color: #f8f9fa;
  color: #495057;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e9ecef;
    color: #212529;
  }

  &.active {
    background-color: #0d6efd;
    color: #fff;
  }
`;

const SmallLoader = styled.div`
  margin-bottom: 1rem;

  .sk-folding-cube {
    transform: scale(0.5);
  }
`;

const VISIBLE_NOTIFICATION_GROUPS = new Set(["Akku Wartung", "Anstehende Wartungen"]);

const useNotificationGroups = () => {
  const dispatch = useDispatch();

  const { notification_groups: groups, isLoading } = useSelector(
    (state: any) => state.notificationGroups,
    shallowEqual
  );

  useEffect(() => {
    if (!isLoading && !groups?.length) {
      dispatch(fetchNotificationGroups(1));
    }
  }, [dispatch, isLoading, groups?.length]);

  const visibleGroups = (groups ?? []).filter((group: any) => {
    if (!group) return false;
    return VISIBLE_NOTIFICATION_GROUPS.has(group.name);
  });

  const allGroups = [
    { slug: 'all', name: 'Alle Benachrichtigungen' },
    ...visibleGroups
  ];

  return { allGroups, isLoading };
};

const GroupNotificationLinks: React.FC = () => {
  const { allGroups, isLoading } = useNotificationGroups();

  if (isLoading && allGroups.length === 1) {
    return (
      <SmallLoader>
        <Loader />
      </SmallLoader>
    );
  }

  return (
    <GroupLinksContainer>
      {allGroups.map(({ slug, name }) => {
        const path =
          slug === 'all' ? '/notifications/all' : `/notifications/group/${slug}`;

        return (
          <StyledNavLink
            key={slug}
            to={path}
            activeClassName="active"
            exact
          >
            {name}
          </StyledNavLink>
        );
      })}
    </GroupLinksContainer>
  );
};

export default React.memo(GroupNotificationLinks);
